import { useEffect, useState } from 'react'
import { login, authPolicy, KbError, type AuthPolicy } from '../api/client'
import type { Session } from '../api/client'
import Icon from '../icons'
import '../styles/signin.css'

/**
 * 登录使用服务器当前账号记录。
 * 成功后换得 HS256 JWT，后续请求带 Authorization: Bearer（技术方案 §5.1）。
 */
export default function SignIn({ onSignIn, notice }: { notice?: string; onSignIn: (session: Session) => void }) {
  const [principalId, setPrincipalId] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [policy,setPolicy]=useState<AuthPolicy|null>(null),[policyError,setPolicyError]=useState(''),[retryAt,setRetryAt]=useState(0),[now,setNow]=useState(Date.now()),[showPassword,setShowPassword]=useState(false)
  useEffect(()=>{void authPolicy().then(setPolicy).catch(()=>setPolicyError('暂时无法获取登录策略，请检查网络或刷新重试。'));const timer=setInterval(()=>setNow(Date.now()),1000);return()=>clearInterval(timer)},[])
  const retrySeconds=Math.max(0,Math.ceil((retryAt-now)/1000))
  const [pending, setPending] = useState(false)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if(pending || retrySeconds>0)return
    setPending(true)
    setMessage('')
    try {
      onSignIn(await login(principalId.trim(), password))
    } catch (caught) {
      if(caught instanceof KbError && caught.status===429)setRetryAt(Date.now()+Math.max(1,caught.retryAfterSeconds||60)*1000)
      setPassword('')
      setMessage(caught instanceof KbError ? caught.message : '无法连接登录服务，请检查网络后重试')
    } finally {
      setPending(false)
    }
  }

  return (
    <main className="signin-page account-signin-page">
      <section className="signin-brand-panel" aria-label="佳应科技售前工作空间">
        <svg className="signin-ribbon" viewBox="0 0 1100 1100" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="signin-ribbon-light" x1="200" y1="900" x2="900" y2="300" gradientUnits="userSpaceOnUse">
              <stop stopColor="#3432ff"/><stop offset=".33" stopColor="#087eec"/><stop offset=".6" stopColor="#3ee6ee"/><stop offset=".8" stopColor="#08a1fa"/><stop offset="1" stopColor="#2927ef"/>
            </linearGradient>
            <linearGradient id="signin-ribbon-edge" x1="100" y1="850" x2="1000" y2="300" gradientUnits="userSpaceOnUse"><stop stopColor="#1820e4"/><stop offset=".6" stopColor="#89f8ff"/><stop offset="1" stopColor="#246bff"/></linearGradient>
          </defs>
          {Array.from({length:18},(_,index)=><path key={index} d="M -200 850 C 190 1150 1060 880 940 450 C 860 160 550 180 470 420 C 350 760 820 830 1190 550" transform={`translate(${index * 6 - 45} ${-index * 11 + 90})`} stroke="url(#signin-ribbon-light)" strokeWidth="22" opacity={.7 + index * .016}/>) }
          <path d="M -200 850 C 190 1150 1060 880 940 450 C 860 160 550 180 470 420 C 350 760 820 830 1190 550" transform="translate(57 -97)" stroke="url(#signin-ribbon-edge)" strokeWidth="2" opacity=".8"/>
        </svg>
        <div className="signin-brand-copy"><span className="signin-brand-kicker">PRESALES AI WORKSPACE</span><h2>让每一份经验，<br/>成为下一份方案。</h2><p>连接知识与需求，让好方案有据可循。</p></div>
      </section>
      <section className="signin-access-panel" aria-labelledby="signin-title">
        <div className="signin-form-wrap">
          <header className="signin-form-heading"><div className="signin-product-identity"><span className="signin-product-mark" aria-hidden="true"><Icon name="kb" size={18}/></span><span className="signin-product-company">佳应科技</span><span className="signin-product-divider" aria-hidden="true"/><span className="signin-product-caption">售前工作空间</span></div><h1 id="signin-title">售前 AI 知识库</h1><p>连接资料、洞察需求，让方案有据可循。</p></header>
          <div className="signin-login-heading"><h2>登录工作空间</h2><span>使用组织账号继续</span></div>
          <form className="signin-form" method="post" action="/auth/login" onSubmit={submit}>
          {notice ? <div className="signin-notice" role="status"><Icon name="shield" size={16}/><span>{notice}</span></div> : null}
          <label className="field">
            <span className="field-label">账号</span>
            <span className="signin-input-wrap"><Icon name="accounts" size={19}/>
            <input
              id="principal-id"
              maxLength={64}
              name="principal_id"
              value={principalId}
              onChange={(event) => setPrincipalId(event.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="请输入组织账号"
              disabled={pending}
              required
            />
            </span>
          </label>
          <label className="field">
            <span className="field-label">密码</span>
            <span className="signin-input-wrap"><Icon name="key" size={19}/>
            <input
              id="password"
              aria-label="密码"
              maxLength={1024}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="请输入密码"
              disabled={pending}
              required
            />
            <button className="signin-password-toggle" type="button" aria-label={showPassword ? '隐藏密码' : '显示密码'} aria-pressed={showPassword} disabled={pending} onClick={()=>setShowPassword(!showPassword)}><Icon name="eye" size={19}/>{showPassword ? <span className="signin-eye-slash"/> : null}</button>
            </span>
          </label>
          {policyError ? <div className="note warn" role="status">{policyError}</div> : null}
          {message ? <div className="note danger" role="alert">{message}</div> : null}
          <button type="submit" className="signin-submit" disabled={pending || retrySeconds>0}>
            <span>{pending ? '登录中…' : retrySeconds>0 ? `${retrySeconds} 秒后重试` : '登录工作空间'}</span><span aria-hidden="true">↗</span>
          </button>
          <p className="signin-help">忘记密码或无法登录？<span>请联系管理员</span></p>
          <details className="signin-security"><summary><Icon name="shield" size={15}/>登录与账号安全<span aria-hidden="true">＋</span></summary><p>{policy ? `登录最长有效 ${Math.round(policy.session_ttl_seconds/3600)} 小时，${Math.round(policy.idle_timeout_seconds/60)} 分钟无操作自动退出。连续失败 ${policy.failed_attempt_limit} 次将限制登录 ${Math.round(policy.lockout_seconds/60)} 分钟。` : '仅限已启用且在有效期内的组织账号。账号停用或到期，请联系管理员处理。'}</p></details>
          </form>
        </div>
      </section>
    </main>
  )
}
